import { CheckboxFormField, IFormRender } from '@busybusy/webapp-react-ui';
import useJsaReportsEnabled from 'containers/jsa-reports/hooks/useJsaReportsEnabled/useJsaReportsEnabled';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import { t } from 'i18next';
import { compact } from 'lodash';
import { useFeatureFlags } from 'utils/features';
import { ICreatePositionsFormData } from '../CreatePositionForm';

export interface ICreatePositionSafetySectionProps {
  form: IFormRender<ICreatePositionsFormData>;
}

const CreatePositionSafetySection = ({ form }: ICreatePositionSafetySectionProps) => {
  const isSafetyIncidentsEnabled = useFeatureFlags('SAFETY_INCIDENTS');
  const isSafetyInspectionsEnabled = useFeatureFlags('SAFETY_INSPECTIONS');
  const isSafetyObservationsEnabled = useFeatureFlags('SAFETY_OBSERVATIONS');
  const isSafetyTrainingsEnabled = useFeatureFlags('SAFETY_TRAINING');
  const isJsaReportsEnabled = useJsaReportsEnabled();

  const safetySectionDetails = compact([
    isSafetyIncidentsEnabled && {
      name: 'createIncident',
      title: t('Create Incident Reports'),
      detail: t('File incident reports for self and others.'),
    },
    isSafetyIncidentsEnabled && {
      name: 'manageIncident',
      title: t('Manage Incident Reports'),
      detail: t('View, edit, delete, and review all incident reports.'),
    },
    isSafetyInspectionsEnabled && {
      name: 'performInspections',
      title: t('Perform Safety Inspections'),
      detail: t(
        'Allow the user to create inspections for themselves and perform inspections assigned to them by others.'
      ),
    },
    isSafetyInspectionsEnabled && {
      name: 'manageInspections',
      title: t('Manage Safety Inspections'),
      detail: t('View, assign, and delete inspections for others. Manage inspection checklists.'),
    },
    isSafetyObservationsEnabled && {
      name: 'reportObservations',
      title: t('Report Safety Observations'),
      detail: t(
        'Allow the user to report safety concerns they may observer, as well as any positive safety obsertaions.'
      ),
    },
    isSafetyObservationsEnabled && {
      name: 'manageObservations',
      title: t('Manage Observations'),
      detail: t('Allow the user to view and process dafety observations submitted by other employees.'),
    },
    isSafetyTrainingsEnabled && {
      name: 'performTraining',
      title: t('Perform Trainings'),
      detail: t(
        'Allow the user to be assigned to conduct safety training meetings. They can also create new meetings but can only assign themselves as the instructor.'
      ),
    },
    isSafetyTrainingsEnabled && {
      name: 'manageTraining',
      title: t('Manage Trainings'),
      detail: t(
        'Allow the user to assign other instructors to lead training meetings. They can also view, edit, and delete meetings created by others.'
      ),
    },
    isJsaReportsEnabled && {
      name: 'createSafetyAnalysisReports',
      title: t('Create JSA Reports'),
      detail: t('Allow the user to create JSA reports. They can also edit and delete their own reports.'),
    },
    isJsaReportsEnabled && {
      name: 'manageSafetyAnalysisReports',
      title: t('Manage JSA Reports'),
      detail: t('Allow the user to edit and delete reports created by others.'),
    },
  ]);

  const handlePermissionChange = (name: string, value: boolean) => {
    if (name === 'createIncident' && value === false) {
      form.setFormData({ ...form.state.data, manageIncident: false });
    } else if (name === 'performInspections' && value === false) {
      form.setFormData({ ...form.state.data, manageInspections: false });
    } else if (name === 'reportObservations' && value === false) {
      form.setFormData({ ...form.state.data, manageObservations: false });
    } else if (name === 'performTraining' && value === false) {
      form.setFormData({ ...form.state.data, manageTraining: false });
    } else if (name === 'createSafetyAnalysisReports' && value === false) {
      form.setFormData({ ...form.state.data, manageSafetyAnalysisReports: false });
    }
  };

  return (
    <>
      <SectionHeader className="mb-6">{t('Safety')}</SectionHeader>
      {safetySectionDetails.map((checkPermission) => (
        <div key={checkPermission.name}>
          <CheckboxFormField
            key={checkPermission.name}
            name={checkPermission.name}
            form={form}
            label={
              <HeaderText title={checkPermission.title}>
                <span>{checkPermission.detail}</span>
              </HeaderText>
            }
            onChange={(value) => handlePermissionChange(checkPermission.name, value)}
            disabled={
              (checkPermission.name === 'manageIncident' && !form.state.data.createIncident) ||
              (checkPermission.name === 'manageInspections' && !form.state.data.performInspections) ||
              (checkPermission.name === 'manageObservations' && !form.state.data.reportObservations) ||
              (checkPermission.name === 'manageTraining' && !form.state.data.performTraining) ||
              (checkPermission.name === 'manageSafetyAnalysisReports' && !form.state.data.createSafetyAnalysisReports)
            }
          />
        </div>
      ))}
    </>
  );
};

export default CreatePositionSafetySection;
